import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import ArticleVideo from './ArticleVideo';
import ArticleAsset from './ArticleAsset';
import EmbeddablePlayer from './EmbeddablePlayer';
import MainCtaButton from '../button/MainCtaButton';

import styled from 'styled-components';
import { media } from '../../../styles/style-utils';
import { legacyType } from '../../../styles/typography';

const Content = styled.div`
  padding-bottom: calc(var(--fr) * 2.75);
  margin-bottom: calc(var(--fr) * 4);
  position: relative;
  text-align: left;

  ::after {
    background-color: var(--c-shark);
    bottom: 0;
    content: '';
    height: calc(var(--fr) / 2);
    left: 0;
    position: absolute;
    width: var(--fr);
  }

  h2 {
    font-size: var(--fs-xl);
    font-weight: 500;
    ${legacyType('xl', 'display')};
    margin-bottom: calc(var(--fr));

    ${media.mediumLarge`
      margin-bottom: calc(var(--fr) * 2);
    `}
  }

  p {
    margin-top: calc(var(--fr) * 2);
    margin-bottom: calc(var(--fr) * 2);
    font-size: var(--fs-sm);
    ${legacyType('sm', 'body')};
  }

  a {
    text-decoration: underline;
  }

  blockquote {
    border-left: 4px solid var(--c-shark);
    font-size: var(--fs-s);
    font-weight: 500;
    margin-bottom: var(--fr);
    margin-left: 0;
    margin-right: 0;
    margin-top: calc(var(--fr) * 2);
    padding-left: calc(var(--fr) * 1.5);

    ${media.mediumLarge`
      margin-left: calc(var(--fr) * 1.875);
      margin-right: calc(var(--fr) * 3.5);
    `};

    p {
      margin: 0;
    }
  }

  ul {
    list-style-type: disc;
    padding-left: 1rem;
    margin-top: 0;
    margin-bottom: 0;

    p {
      margin: 0;
    }
  }
`;

const InlineVideoContainer = styled.div`
  display: inline;
  float: left;
  width: 292px;
  height: 147px;
  margin-right: 7px;
`;

const options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: node => {
      if (!get(node, 'data.target.fields')) {
        return null;
      }

      const { description, file } = node.data.target.fields;

      return (
        <ArticleAsset
          description={description}
          url={get(file, 'url')}
          contentType={file.contentType}
        />
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      const { target } = node.data;
      switch (target.sys.contentType.sys.id) {
        case 'magazinePlayer':
          return (
            <EmbeddablePlayer
              id={get(target, 'fields.playlistId')}
              type={get(target, 'fields.playerType')}
            />
          );
        case 'magazineVideo':
          if (get(target, 'fields')) {
            return <ArticleVideo {...target.fields} />;
          }
          return null;
        case 'landingPagesCtaButton':
          return <MainCtaButton content={target.fields} />;
        default:
          return null;
      }
    },
    [INLINES.EMBEDDED_ENTRY]: node => {
      const { target } = node.data;
      if (target.sys.contentType.sys.id === 'magazineVideo' && target.fields) {
        // No autoplay allowed on inline videos
        return (
          <InlineVideoContainer>
            <ArticleVideo {...target.fields} autoPlay={false} />
          </InlineVideoContainer>
        );
      }
      return null;
    },
  },
};

const ArticleBody = ({ body }) => {
  return <Content>{documentToReactComponents(body, options)}</Content>;
};

ArticleBody.propTypes = {
  body: PropTypes.object,
};

export default ArticleBody;



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/article/ArticleBody.js